import React from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  Flag,
  UserPlus,
  CheckSquare,
  CheckCircle,
  LogIn,
  LogOut,
  FileText,
  Upload,
  Mail,
  Phone,
  MessageSquare,
  Activity,
  User,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AuditLog, ACTIVITY_TYPE_CONFIGS } from '@/types/auditLog';

interface AuditLogCardProps {
  log: AuditLog;
  onClick?: (log: AuditLog) => void;
}

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  Flag,
  UserPlus,
  CheckSquare,
  CheckCircle,
  LogIn,
  LogOut,
  FileText,
  Upload,
  Mail,
  Phone,
  MessageSquare,
  Activity,
  User,
};

// Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Format time of day
const formatTimeOfDay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Get change summary
const getChangeSummary = (changes?: Record<string, { old: any; new: any }>): string | null => {
  if (!changes || Object.keys(changes).length === 0) return null;

  const summaries: string[] = [];
  for (const [key, value] of Object.entries(changes)) {
    const fieldName = key.replace(/([A-Z])/g, ' $1').trim();
    if (value.old !== undefined && value.new !== undefined) {
      summaries.push(`${fieldName}: ${value.old} -> ${value.new}`);
    }
  }

  return summaries.length > 0 ? summaries.join(', ') : null;
};

export const AuditLogCard: React.FC<AuditLogCardProps> = ({ log, onClick }) => {
  const config = ACTIVITY_TYPE_CONFIGS[log.type] || ACTIVITY_TYPE_CONFIGS.DEFAULT;
  const IconComponent = iconMap[config.icon] || Activity;
  const changeSummary = getChangeSummary(log.changes);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      className={cn(
        'bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all cursor-pointer',
        onClick && 'hover:border-blue-300'
      )}
      onClick={() => onClick?.(log)}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn('p-2 rounded-lg', config.bgColor)}>
          <IconComponent className={cn('w-4 h-4', config.color)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {config.label} - {log.description || 'Activity'}
            </h3>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {formatTimeOfDay(log.createdAt)}
            </span>
          </div>

          {/* User */}
          {log.user && (
            <p className="text-xs text-gray-600 mt-1">
              {log.user.name} <span className="text-gray-400">({log.user.email})</span>
            </p>
          )}

          {/* Change Summary */}
          {changeSummary && (
            <p className="text-xs text-gray-500 mt-2 line-clamp-2 bg-gray-50 px-2 py-1 rounded">
              {changeSummary}
            </p>
          )}

          {/* Entity Link */}
          {log.entityType && log.entityId && (
            <div className="flex items-center gap-1 mt-2">
              <span className="text-xs text-gray-400">{log.entityType}</span>
              <ExternalLink className="w-3 h-3 text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* View Details */}
      {onClick && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            View Details
          </button>
        </div>
      )}
    </motion.div>
  );
};
